import React from 'react';
import { TextField } from '@material-ui/core';

/**
 * Componente de Input Monetário que funciona como uma calculadora
 * Insere os dígitos da direita para a esquerda
 * Exemplo: 0,00 -> 0,01 -> 0,15 -> 1,54 -> 15,41 -> 154,12 -> 1.541,28
 */
const MonetaryInput = ({ 
  value, 
  onChange, 
  name,
  label,
  placeholder,
  error,
  helperText,
  required,
  fullWidth = true,
  variant = "outlined",
  margin = "dense",
  ...otherProps 
}) => {
  // Converte o valor armazenado (número ou string) para centavos
  const valueToNumber = (val) => {
    if (!val || val === '') return 0;
    
    // Se já é número, multiplica por 100 para ter centavos
    if (typeof val === 'number') {
      return Math.round(val * 100);
    }
    
    // Se é string, remove tudo exceto dígitos
    const cleanValue = String(val).replace(/\D/g, '');
    return parseInt(cleanValue) || 0;
  };

  // Formata centavos para exibição (R$ 1.234,56)
  const formatCurrency = (cents) => {
    const value = cents / 100;
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Valor atual em centavos
  const currentCents = valueToNumber(value);
  const displayValue = formatCurrency(currentCents);

  const handleKeyDown = (e) => {
    // Permitir: Backspace, Delete, Tab, Escape, Enter
    if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
        // Permitir: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true) ||
        // Permitir: Home, End, Left, Right
        (e.keyCode >= 35 && e.keyCode <= 39)) {
      return;
    }

    // Garantir que é um número (0-9) do teclado principal ou numérico
    if ((e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
      return;
    }
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;
    
    // Remove todos os caracteres não numéricos
    const numbers = inputValue.replace(/\D/g, '');
    
    if (numbers === '') {
      // Se vazio, volta para 0
      onChange({
        target: {
          name: name,
          value: 0
        }
      });
      return;
    }

    // Converte para centavos (número inteiro)
    const cents = parseInt(numbers);
    
    // Converte centavos para valor decimal (divide por 100)
    const decimalValue = cents / 100;
    
    // Chama o onChange com o valor numérico
    onChange({
      target: {
        name: name,
        value: decimalValue
      }
    });
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const numbers = pastedText.replace(/\D/g, '');
    
    if (numbers === '') return;

    const cents = parseInt(numbers);
    const decimalValue = cents / 100;
    
    onChange({
      target: {
        name: name,
        value: decimalValue
      }
    });
  };

  return (
    <TextField
      {...otherProps}
      name={name}
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      label={label}
      placeholder={placeholder || 'R$ 0,00'}
      error={error}
      helperText={helperText}
      required={required}
      fullWidth={fullWidth}
      variant={variant}
      margin={margin}
      inputProps={{
        inputMode: 'numeric',
        style: { textAlign: 'right' }
      }}
    />
  );
};

export default MonetaryInput;
